<template>
  <div :class="colSpan">
    <h3
      v-if="field?.display"
      class="w-full font-bold"
    >
      {{ field.display }}
    </h3>

    <p v-if="instructions || field.instructions" class="mt-2">
      {{ instructions || field.instructions }}
    </p>
    <div
      :class="{
        'mt-5': field?.display || instructions || field.instructions,
      }"
    >
      <Field
        v-slot="{ handleChange, value }"
        :name="name || handle || field.handle"
        :label="label || field.label || field.display"
        :rules="rulesAsString"
      >
        <div
          class="flex items-start"
          :class="[
            multiple
              ? 'flex-col space-y-2'
              : `
                flex-col
                sm:flex-row sm:items-center
              `,
          ]"
        >
          <input
            :id="`${field.handle}-actual-btn`"
            type="file"
            hidden
            :multiple="multiple"
            @change="handleChange"
          />
          <!-- our custom upload button -->
          <label
            :for="`${field.handle}-actual-btn`"
            class="
              relative isolate flex w-max cursor-pointer items-center
              overflow-hidden rounded-sm bg-gray-100 px-3 py-1 text-sm font-bold
              transition-colors
              before:absolute before:left-0 before:top-0 before:-z-10
              before:h-full before:w-0 before:bg-white/10 before:transition-all
              focus-visible:border-white focus-visible:ring
              focus-visible:before:w-full
              hover:before:w-full
              sm:px-4 sm:py-2
            "
          >
            Datei hochladen
          </label>

          <!-- name of file chosen -->
          <span
            v-if="!multiple"
            id="file-chosen"
            class="
              mt-4 break-all
              sm:ml-5 sm:mt-0
            "
          >
            {{ value?.name || initialFileName || 'Keine Datei ausgewählt' }}
          </span>
          <template v-else>
            <span
              v-for="(file, index) in value"
              :id="`file-chosen-${index}`"
              :key="index"
              class="font-bolt mt-2 flex items-center break-all"
            >
              {{ file.name }}
            </span>
          </template>
        </div>
      </Field>
    </div>

    <!-- ERROR -->
    <TransitionExpand
      class="transition-all duration-300 ease-in-out"
      :open="!!error"
    >
      <p v-if="error" class="hyphens-auto pt-1 text-sm text-red-500">
        {{ error }}
      </p>
    </TransitionExpand>
  </div>
</template>

<!-- eslint-disable-next-line vue/block-lang -->
<script setup>
import { Field } from 'vee-validate'

const props = defineProps({
  field: {
    type: Object,
    default: null,
  },

  label: {
    type: String,
    default: '',
  },

  handle: {
    type: String,
    default: '',
  },

  name: {
    type: String,
    default: '',
  },

  btnLabel: {
    type: String,
    default: null,
  },

  rules: {
    type: [Array, String],
    default: '',
  },

  error: {
    type: String,
    default: null,
  },

  instructions: {
    type: String,
    default: null,
  },

  initialFileName: {
    type: String,
    default: '',
  },
})

const multiple = props.field?.config?.max_files !== 1

// filter out rules min and max for files
let { rules } = props

rules = rules.filter((rule) => {
  if (typeof rule === 'string') {
    return !rule.includes('min') && !rule.includes('max')
  }
  return true
})

// add custom rule to check amount max files
const maxFiles = props.field?.config?.max_files
if (maxFiles) {
  rules.push(`max_files:${maxFiles}`)
}

// add custom rule to check amount min files
const minFiles = props.field?.config?.min_files
if (minFiles && minFiles > 0) {
  // Then the field is required
  if (!rules.includes('required')) {
    rules.push('required')
  }

  rules.push(`min_files:${minFiles}`)
}

const rulesAsString = useFilterVeeValidateRules(rules)

const colSpan = computed(() => {
  const width = parseInt(props.width) || props.field?.width || 100

  switch (width) {
    case 25:
      return 'md:col-span-6 lg:col-span-3 col-span-12'
    case 33:
      return 'md:col-span-6 lg:col-span-4 col-span-12'
    case 50:
      return 'md:col-span-6 lg:col-span-6 col-span-12'
    case 66:
      return 'md:col-span-6 lg:col-span-8 col-span-12'
    default:
      return 'lg:col-span-12 col-span-12'
  }
})
</script>
